import * as React from 'react'

import {
  Box,
  Icon,
  type SortDirection,
  Table,
  Tag,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { orderBy } from 'lodash'
import moment from 'moment'

import { observer } from 'mobx-react'

import createMemo from 'modules/core/hooks/createMemo'
import i18n from 'modules/core/lib/i18n'

import EmployeeLabelWithFallback from 'modules/reviews/components/EmployeeLabel/EmployeeLabelWithFallback'
import { getGroupName } from 'modules/reviews/lib/getGroupName'
import { getReviewLabel } from 'modules/reviews/lib/getReviewLabel'
import { getType } from 'modules/reviews/lib/reviewQuestionGroupState'
import type QuestionGroup from 'modules/reviews/store/models/QuestionGroup'

function TooltipSettings() {
  const [open, toggle] = useToggle()

  return (
    <Tooltip.Alt
      attachment="top center"
      label={i18n.t('surveys.review.not_closed_yet')}
      open={open}
      toggle={toggle}
    >
      <Box alignItems="flexEnd">
        <Icon.Medium icon="arrow-right" />
      </Box>
    </Tooltip.Alt>
  )
}

interface RowProps {
  isAnonymous: boolean
  questionGroup: QuestionGroup
  settings: React.ReactNode
}

function Row({ isAnonymous, questionGroup, settings }: RowProps) {
  const date = questionGroup.attributes.editable_until

  return (
    <>
      <Table.Cell colSpan={3}>
        <EmployeeLabelWithFallback user={questionGroup.respondent} isAnonymous={isAnonymous} />
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Text.Normal>{getGroupName(questionGroup.respondentGroup)}</Text.Normal>
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Box flexDirection="row">
          <QuestionGroupState questionGroup={questionGroup} />
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Text.Normal>{date ? moment(date).format('L') : '-'}</Text.Normal>
      </Table.Cell>
      <Table.Cell>{settings}</Table.Cell>
    </>
  )
}

interface StateProps {
  questionGroup: QuestionGroup
}

function QuestionGroupState({ questionGroup }: StateProps) {
  const labelType = getType(questionGroup)
  const label = getReviewLabel(questionGroup.attributes.state)

  return (
    <Tag leftItem="circle" capitalize showCircle type={labelType}>
      {label}
    </Tag>
  )
}

const useFilter = createMemo(
  (isAnonymous: boolean, questionGroups: QuestionGroup[], debouncedSearch: string) =>
    questionGroups.filter((questionGroup) => {
      if (isAnonymous) return true

      return questionGroup.respondent?.matchesFullName(debouncedSearch)
    })
)
interface Props {
  search: string
  questionGroups: QuestionGroup[]
  computeRoute: (questionGroup: QuestionGroup) => string
  isAnonymous: boolean
  direction: SortDirection
  attribute: string
}

const orderQuestionGroup = (questionGroup: QuestionGroup, attribute: string) => {
  switch (attribute) {
    case 'review_type':
      return getGroupName(questionGroup?.respondentGroup)
    case 'state':
      return getReviewLabel(questionGroup.attributes.state)
    default:
      return questionGroup?.respondent?.fullName()
  }
}

function Body({ isAnonymous, questionGroups, search, computeRoute, direction, attribute }: Props) {
  const filteredQuestionGroups = useFilter(isAnonymous, questionGroups, search)
  const sortedQuestionGroups = orderBy(
    filteredQuestionGroups,
    (questionGroup: QuestionGroup) => orderQuestionGroup(questionGroup, attribute),
    [direction]
  )

  return (
    <Table.Body>
      {sortedQuestionGroups.map((questionGroup: QuestionGroup) => {
        if (!questionGroup.canSeeAnswers) {
          return (
            <Table.Row key={questionGroup.get('id')}>
              <Row
                isAnonymous={isAnonymous}
                questionGroup={questionGroup}
                settings={<TooltipSettings />}
              />
            </Table.Row>
          )
        }

        return (
          <Table.Row.Router key={questionGroup.get('id')} to={computeRoute(questionGroup)}>
            <Row
              isAnonymous={isAnonymous}
              questionGroup={questionGroup}
              settings={
                <Box alignItems="flexEnd" aria-label="Access review answer">
                  <Icon.Medium icon="arrow-right" />
                </Box>
              }
            />
          </Table.Row.Router>
        )
      })}
    </Table.Body>
  )
}

export default observer(Body)
